import { Check, Close, Flip, FormatColorFill, HourglassEmpty } from '@mui/icons-material';
import { Button, IconButton, Slider } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SketchPicker } from 'react-color';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { useTranslation } from 'next-i18next';
import getCroppedImg, { getCroppedImgWithoutRotate } from '../../utils/cropImage';
import VyModalCropper from './VyModalCropper';
import VyTooltip from './VyTooltip';
import VyTextField from './VyTextField';
import useIntercom from '../../hooks/useIntercom';

interface VyCropperProps {
  image: string;
  open?: boolean;
  aspect?: number;
  customAspectRatio?: boolean;
  size?: CropSize;
  shape?: 'rect' | 'round';
  onCrop?: (croppedImage: Blob) => void;
  onCancel?: () => void;
  onSkip?: () => void;
  withRotation?: boolean;
}

export interface CropSize {
  width: number;
  height: number;
}

let croppedAreaPixels: Area;

export const VyCropper = ({
  image,
  open = false,
  onCrop,
  size,
  onCancel,
  shape,
  withRotation = true,
  aspect = 1,
  customAspectRatio,
  onSkip,
}: VyCropperProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1.0);
  const [rotation, setRotation] = useState(0);
  const [flip, setFlip] = useState({ horizontal: false, vertical: false });
  const [aspectRatio, setAspectRatio] = useState([16, 9]);
  const intercom = useIntercom();

  const [loading, setLoading] = useState<boolean>();
  const [backgroundColorPicker, setBackgroundColorPicker] = useState<{ open: boolean; color: string }>({
    open: false,
    color: '#00000000',
  });
  const backgroundColorPickerRef = useRef<any>();
  const { t } = useTranslation(['account', 'errors', 'pagebuilder']);

  const handleDocumentClickBg = useCallback((e: MouseEvent) => {
    if (!backgroundColorPickerRef.current?.contains(e.target))
      setBackgroundColorPicker((picker) => {
        return { ...picker, open: false };
      });
  }, []);

  async function updateImage(): Promise<Blob | null> {
    if (croppedAreaPixels) {
      const value: any = withRotation
        ? await getCroppedImg(image, croppedAreaPixels, rotation, flip, backgroundColorPicker.color)
        : await getCroppedImgWithoutRotate(image, croppedAreaPixels, rotation, flip, backgroundColorPicker.color);
      if (value) {
        return value;
      }
      return null;
    }
    return null;
  }

  function handleAspectRatioChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) {
    const newRatio = [...aspectRatio];
    newRatio[index] = Number(e.target.value);
    if (!newRatio || newRatio.reduce((a, b) => a + b) <= 0 || newRatio.some((number) => isNaN(number))) return;
    setAspectRatio(newRatio);
  }

  async function handleConfirm() {
    setLoading(true);
    const img = await updateImage();
    if (img) {
      onCrop?.(img);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (backgroundColorPicker.open) {
      document.addEventListener('click', handleDocumentClickBg, true);
    } else {
      document.removeEventListener('click', handleDocumentClickBg, true);
    }
  }, [backgroundColorPicker.open]);

  useEffect(() => {
    if (open) {
      intercom.hideButton();
    } else {
      intercom.showButton();
    }

    return () => {
      intercom.showButton();
    };
  }, [open]);

  return (
    <VyModalCropper open={open} onClose={onCancel}>
      {() => (
        <>
          <Cropper
            image={image}
            crop={crop}
            transform={[
              `translate(${crop.x}px, ${crop.y}px)`,
              `rotateZ(${rotation}deg)`,
              `rotateY(${flip.horizontal ? 180 : 0}deg)`,
              `rotateX(${flip.vertical ? 180 : 0}deg)`,
              `scale(${zoom})`,
            ].join(' ')}
            zoom={zoom}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            minZoom={0.001}
            cropSize={size}
            aspect={customAspectRatio ? aspectRatio[0] / aspectRatio[1] : aspect}
            cropShape={shape}
            rotation={rotation}
            onRotationChange={setRotation}
            onCropComplete={async (_, pixels: Area) => {
              croppedAreaPixels = pixels;
            }}
            style={{
              containerStyle: {
                backgroundColor: backgroundColorPicker.color,
                transform: 'scale(0.8)',
              },
            }}
          />

          <div className="flex z-50 flex-col justify-center items-center p-2 mt-auto w-full bg-white bg-opacity-100 md:flew-row">
            <div className="flex flex-col items-center px-16 w-full lg:flex-row">
              <p className="px-5">ZOOM</p>
              <Slider
                value={zoom}
                min={0.001}
                max={3}
                step={0.01}
                aria-labelledby="Zoom"
                onChange={(e, val) => setZoom(val as number)}
                className="max-w-full"
              />
              {withRotation && (
                <>
                  <p className="px-5">ROTATION</p>
                  <Slider
                    value={rotation}
                    min={0}
                    max={360}
                    step={1}
                    aria-labelledby="Rotation"
                    onChange={(e, val) => setRotation(val as number)}
                    className="max-w-full"
                  />
                </>
              )}

              <div className="flex flex-col items-center px-2 md:flex-row">
                <div className="flex">
                  {withRotation && (
                    <VyTooltip title={t('mirror-horizontal')}>
                      <IconButton
                        aria-label="Flip Horizontal"
                        onClick={() => {
                          setFlip((prev) => ({ horizontal: !prev.horizontal, vertical: prev.vertical }));
                          setRotation((prev) => 360 - prev);
                        }}
                        size="large">
                        <Flip />
                      </IconButton>
                    </VyTooltip>
                  )}
                  {withRotation && (
                    <VyTooltip title={t('mirror-vertical')}>
                      <IconButton
                        aria-label="Flip Vertical"
                        onClick={() => {
                          setFlip((prev) => ({ horizontal: prev.horizontal, vertical: !prev.vertical }));
                          setRotation((prev) => 360 - prev);
                        }}
                        size="large">
                        <Flip style={{ transform: 'rotate(90deg)' }} />
                      </IconButton>
                    </VyTooltip>
                  )}

                  {customAspectRatio && (
                    <VyTooltip title={<span className="whitespace-pre-wrap">{t('pagebuilder:aspect-ratio-common-values')}</span>}>
                      <div className="flex gap-1 items-center">
                        <VyTextField size="small" value={`${aspectRatio[0]}`} className="w-12" onChange={(e) => handleAspectRatioChange(e, 0)} />
                        <span className="text-lg font-bold">:</span>
                        <VyTextField size="small" value={`${aspectRatio[1]}`} className="w-12" onChange={(e) => handleAspectRatioChange(e, 1)} />
                      </div>
                    </VyTooltip>
                  )}
                  <VyTooltip title={t('choose-color')}>
                    <div>
                      <IconButton
                        aria-label="Flip Vertical"
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setBackgroundColorPicker((picker) => {
                            return { ...picker, open: true };
                          });
                        }}
                        size="large">
                        <FormatColorFill />
                      </IconButton>
                      <div ref={backgroundColorPickerRef} style={{ position: 'fixed', bottom: 10, zIndex: 2 }}>
                        {backgroundColorPicker.open && (
                          <div className="picker">
                            <SketchPicker
                              color={backgroundColorPicker.color}
                              onChange={(c) => {
                                console.log('color ; ', c.hex);
                                setBackgroundColorPicker((picker) => {
                                  return { ...picker, color: c.hex };
                                });
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </VyTooltip>
                </div>

                <div className="flex">
                  <VyTooltip title={t('cancel')}>
                    <IconButton onClick={onCancel} size="large">
                      <Close />
                    </IconButton>
                  </VyTooltip>
                  <VyTooltip title={t('confirm')}>
                    <IconButton color="primary" onClick={handleConfirm} size="large">
                      {!loading ? <Check /> : <HourglassEmpty className="animate-spin" />}
                    </IconButton>
                  </VyTooltip>
                  {onSkip && (
                    <VyTooltip title={t('save-without-editing')}>
                      <Button variant="text" size="small" onClick={onSkip} sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        {t('skip')}
                      </Button>
                    </VyTooltip>
                  )}
                </div>
              </div>
              {/* FOR DEBUGGING */}
              {/* <img style={{ borderRadius: '5px' }} src={croppedImage ? URL.createObjectURL(croppedImage) : ''} height={50} width={50} /> */}
            </div>
          </div>
        </>
      )}
    </VyModalCropper>
  );
};
